// Задача фоновой загрузки файла: одна форма и для очереди редактора, и для
// «выполнить на месте» (категории/статьи живут без провайдера очереди).
import type { CompressionType } from '@/api/uploadFile'
import type { Block } from '@/types/blocks'

/**
 * Как положить URL в блок. Чистая функция намеренно: задача переживает
 * размонтирование панели, и применяется она к СВЕЖЕМУ серверному блоку в
 * момент коммита. Замыкание на черновик утащило бы в `PUT` (полная замена
 * тела) тот черновик, каким он был в момент броска файла.
 *
 * `null` — «места для картинки в этом блоке нет»: элемент дизайна не нашёлся
 * по id (автор добавил его, но так и не сохранил), id пуст, тип блока не тот.
 * Ветка обязана быть отдельной: блок, вернувшийся неизменным, ушёл бы в `PUT`
 * целиком и получил бы рапорт «сохранено» поверх потерянной картинки.
 */
export type ApplyImageToBlock = (block: Block, url: string) => Block | null

/** Блок-владелец картинки. Приходит ИЗ ТОЧКИ ВЫЗОВА: очередь не должна
 *  угадывать владельца по глобальному выделению — оно к моменту коммита уже
 *  уедет, да и связь «этот `ImageUpload` про этот блок» видна только там. */
export interface BlockUploadTarget {
  blockId: number
  lessonId: number
  /** Место ВНУТРИ блока, если их несколько (id элемента дизайна). У картинки
   *  info-блока место одно, поэтому здесь пусто. */
  placeId?: string
  apply: ApplyImageToBlock
}

/**
 * Идентичность места загрузки. Нужна потому, что «жив ли компонент» — НЕ ответ
 * на вопрос «то же ли это место»: панель блока рендерит редактор без `key`, и
 * переключение на соседний блок того же типа (или на соседний элемент дизайна)
 * реконсилирует то же дерево — экземпляр `ImageUpload` остаётся смонтированным,
 * но его `onChange` уже пишет в ЧУЖОЙ черновик.
 */
export const placeKey = (target: BlockUploadTarget): string =>
  `${target.blockId}:${target.placeId ?? ''}`

/** Коммит URL. `false` — подтверждённого сохранения НЕ было (отказ, упавший
 *  запрос или запись в черновик): старый файл в этом случае трогать нельзя.
 *  `void` считается успехом, как и раньше — так ведёт себя синхронный
 *  `onChange` курса/категории. */
export type CommitUpload = (url: string) => void | boolean | Promise<boolean | void>

export interface UploadTask {
  file: File
  compressionType: CompressionType
  /** URL, который заменяем: удаляется только после подтверждённого коммита. */
  previousUrl: string | null
  /** Путь по умолчанию (курс, урок, маскот, категория, статья) — тот же
   *  `onChange`, что и до очереди. */
  commit: CommitUpload
  /** Владелец коммита — блок урока: у него свой путь (свежее тело + гейты). */
  block?: BlockUploadTarget
  /**
   * На экране ли ещё ТО ЖЕ место загрузки — не «жив ли компонент» (см.
   * `placeKey`). Пока место на месте, коммит остаётся ЕГО (`commit` выше пишет
   * в живой черновик); ушло — картинку сохраняет фоновый путь по цели, снятой
   * при постановке. Задача без этого признака считается осиротевшей: у неё нет
   * никого, кто мог бы принять URL.
   *
   * `keyOf` — чем сравнивать места (по умолчанию `placeKey`). Редактор передаёт
   * ключ через алиасы id: смена источника чтения (черновик ⇄ живой курс) меняет
   * id того же блока, и без этого открытое место считалось бы ушедшим.
   */
  isAlive?: (keyOf?: (target: BlockUploadTarget) => string) => boolean
  /** Прогресс для того, кто бросил файл (`null` — загрузка закончилась). */
  onProgress?: (percent: number | null) => void
}

export interface UploadRunner {
  upload: (
    file: File,
    opts: { compressionType: CompressionType; onProgress: (percent: number | null) => void },
  ) => Promise<string | null>
  removeFile: (url: string) => void
}

/**
 * Единственное место, где живёт порядок «загрузить → закоммитить → удалить
 * старый блоб». Обе реализации очереди зовут именно его, поэтому обещание
 * «старый файл удаляется ТОЛЬКО после прошедшего сохранения» не может
 * разойтись между ними.
 */
export const runUploadTask = async (
  task: UploadTask,
  runner: UploadRunner,
  commit: CommitUpload,
): Promise<void> => {
  const url = await runner.upload(task.file, {
    compressionType: task.compressionType,
    onProgress: (percent) => task.onProgress?.(percent),
  })
  if (!url) return
  const saved = await commit(url)
  if (task.previousUrl && saved !== false) runner.removeFile(task.previousUrl)
}
